import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Form, InputGroup } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import EventCard from "../components/events/EventCard";
import { useDispatch, useSelector } from "react-redux";
import SelectField from "../components/common/SelectField";
import { SET_LOADING } from "../redux/user";

const Events = () => {
  const { user } = useSelector((state) => state.USER_DATA);

  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isMember, setIsMember] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const dispatch = useDispatch();

  const options = [
    { value: "all", label: "All" },
    { value: "upcoming", label: "Upcoming Events" },
    { value: "past", label: "Past Events" },
  ];

  const [selectedOption, setSelectedOption] = useState(options[0]);
  const limit = 12;

  // ------------------------------
  // CHECK USER MEMBERSHIP
  // ------------------------------
  useEffect(() => {
    setIsMember(Boolean(user?.is_membership_active));
  }, [user]);

  // ------------------------------
  // FETCH EVENTS (MAIN FUNCTION)
  // ------------------------------
  const fetchEvents = async (
    type = "upcoming",
    pageNumber = 1,
    searchText = ""
  ) => {
    if (pageNumber === 1) setInitialLoading(true);
    else setLoadingMore(true);

    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(
        `${api_paths.member.EVENTS.EVENT_PAGE.GET_LIST}/${type}?filter=${searchText}&page=${pageNumber}&limit=${limit}&descending=true`
      );

      const data = response.data?.payload?.result?.data || [];
      const total_pages =
        response.data?.payload?.result?.meta?.total_pages || 1;

      // Avoid zero so page comparison stays sane
      setTotalPages(Math.max(1, total_pages));

      // Replace or append events
      if (pageNumber === 1) setEvents(data);
      else setEvents((prev) => [...prev, ...data]);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      if (pageNumber === 1) setInitialLoading(false);
      else setLoadingMore(false);
      dispatch(SET_LOADING(false));
    }
  };

  // ------------------------------
  // INITIAL LOAD
  // ------------------------------
  useEffect(() => {
    fetchEvents(selectedOption.value, 1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------
  // VIEW MORE
  // ------------------------------
  const handleViewMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchEvents(selectedOption.value, nextPage, search);
  };

  // ------------------------------
  // ON SEARCH (with debounce)
  // ------------------------------
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPage(1);
      fetchEvents(selectedOption.value, 1, search);
    }, 500);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ------------------------------
  // ON FILTER CHANGE
  // ------------------------------
  const handleSelectChange = (option) => {
    setSelectedOption(option);
    setPage(1);
    fetchEvents(option.value, 1, search);
  };

  const showViewMore =
    !initialLoading &&
    !loadingMore &&
    page < totalPages &&
    events.length >= limit;

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col lg={12}>
          <div className="rsvp-header text-center">
            <h1 className="inter-bold h1">Events</h1>
            <p>Join us for exciting cultural celebrations and gatherings</p>
          </div>
        </Col>
      </Row>

      {/* SEARCH + FILTER */}
      <Form>
        <Row className="mb-4">
          <Col lg={9} md={8}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search events..."
                value={search}
                onChange={handleSearchChange}
              />
              <InputGroup.Text>
                <CiSearch size={20} />
              </InputGroup.Text>
            </InputGroup>
          </Col>

          <Col lg={3} md={4} className="d-flex">
            <div className="w-100 mt-3 mt-md-0">
              <SelectField
                value={selectedOption}
                onChange={handleSelectChange}
                options={options}
                label={null}
              />
            </div>
          </Col>
        </Row>
      </Form>

      {/* EVENTS GRID */}
      <Row className="g-4 mt-4">
        {/* SKELETON LOADING */}
        {initialLoading &&
          [...Array(6)].map((_, i) => <EventCard key={i} isLoading />)}

        {/* NO EVENTS */}
        {!initialLoading && events.length === 0 && (
          <Col lg={12} className="text-center my-5">
            <p>No events found.</p>
          </Col>
        )}

        {/* EVENT LIST */}
        {!initialLoading &&
          events.map((item) => (
            <EventCard
              key={item._id}
              item={item}
              isLoading={false}
              isMember={isMember}
            />
          ))}

        {/* LOAD MORE SKELETON */}
        {loadingMore &&
          [...Array(3)].map((_, i) => <EventCard key={`sk-${i}`} isLoading />)}

        {/* VIEW MORE BUTTON */}
        {showViewMore && (
          <div className="text-center mt-5">
            <Button onClick={handleViewMore} className="px-5 btn btn-orange-md">
              View More
            </Button>
          </div>
        )}
      </Row>
    </Container>
  );
};

export default Events;
